import { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import { AdminsApi, PermissionsApi } from '../../api/resources';
import { errorMessage } from '../../api/client';

/**
 * Assign granular (direct) permissions to a single admin.
 * The catalogue is whatever the API returns from GET /permissions, so new
 * features show up here automatically.
 */
export default function PermissionsModal({ admin, onClose, onSaved }) {
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(() => new Set(admin.permissions || []));
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    PermissionsApi.catalogue()
      .then(setGroups)
      .catch((err) => setBanner(errorMessage(err, 'Could not load permissions.')));
  }, []);

  const total = useMemo(
    () => (groups || []).reduce((n, g) => n + g.permissions.length, 0),
    [groups]
  );

  const toggle = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const toggleGroup = (perms, on) =>
    setSelected((prev) => {
      const next = new Set(prev);
      perms.forEach((p) => (on ? next.add(p) : next.delete(p)));
      return next;
    });

  const save = async () => {
    setBusy(true);
    setBanner('');
    try {
      await AdminsApi.syncPermissions(admin.id, Array.from(selected));
      onSaved();
    } catch (err) {
      setBanner(errorMessage(err, 'Could not save permissions.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      wide
      title={`Permissions — ${admin.name}`}
      onClose={onClose}
      footer={
        <>
          <span className="muted" style={{ marginRight: 'auto', fontSize: 13 }}>
            {selected.size} / {total} selected
          </span>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !groups}>
            {busy ? 'Saving…' : 'Save permissions'}
          </button>
        </>
      }
    >
      {banner && <div className="notice notice-error">{banner}</div>}

      {admin.is_super_admin && (
        <div className="notice notice-ok">
          This user is a super admin and already holds every permission.
        </div>
      )}

      {!groups && !banner && <div className="spinner">Loading permissions…</div>}

      {groups &&
        groups.map((g) => {
          const allOn = g.permissions.every((p) => selected.has(p));
          return (
            <div className="perm-group" key={g.group}>
              <div className="perm-group-title">
                <span>{g.group}</span>
                <label>
                  <input
                    type="checkbox"
                    checked={allOn}
                    onChange={(e) => toggleGroup(g.permissions, e.target.checked)}
                    disabled={admin.is_super_admin}
                  />
                  Select all
                </label>
              </div>
              <div className="perm-list">
                {g.permissions.map((p) => (
                  <label className="perm-item" key={p}>
                    <input
                      type="checkbox"
                      checked={selected.has(p)}
                      onChange={() => toggle(p)}
                      disabled={admin.is_super_admin}
                    />
                    <code>{p}</code>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
    </Modal>
  );
}
